import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useAllInventoryRequestList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refAllRelatedInventoryRequestsItemTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'requestStringID',
      label: 'ID',
      sortable: true,
    },
    {
      key: 'item',
      label: 'Item',
      sortable: false,
    },
    {
      key: 'requestFileNo',
      label: 'file no',
      sortable: true,
    },
    {
      key: 'category',
      label: 'Category',
      sortable: false,
    },
    {
      key: 'totalQuantity',
      label: 'Qty',
      sortable: true,
    },
    {
      key: 'fileNo',
      label: 'Item File No',
      sortable: true,
    },
    {
      key: 'createdBy',
      label: 'requested by',
      sortable: false,
    },
    { key: 'status', sortable: false },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalInventoryRequests = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'requestStringID')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc === 'true') : true)
  const currentRequest = ref(route.params.id)
  const fileNoQuery = ref([])
  if (Array.isArray(searchQuery)) {
    fileNoQuery.value = route.query.fileNo
  } else if (route.query.fileNo) {
    fileNoQuery.value = [route.query.fileNo]
  }
  const itemFileNoQuery = ref([])
  if (Array.isArray(searchQuery)) {
    itemFileNoQuery.value = route.query.itemFileNo
  } else if (route.query.itemFileNo) {
    itemFileNoQuery.value = [route.query.itemFileNo]
  }
  const singleItemFileNo = ref(route.query.singleItemFileNo ?? null)
  const categoryFilter = ref([])
  if (Array.isArray(route.query.category)) {
    categoryFilter.value = route.query.category
  } else if (route.query.category) {
    categoryFilter.value = [route.query.category]
  }
  const userFilter = ref([])
  if (Array.isArray(route.query.createdBy)) {
    userFilter.value = route.query.createdBy
  } else if (route.query.createdBy) {
    userFilter.value = [route.query.createdBy]
  }
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const inventoryRequestItemData = ref([])
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refAllRelatedInventoryRequestsItemTable.value ? refAllRelatedInventoryRequestsItemTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalInventoryRequests.value,
    }
  })

  const refetchData = () => {
    refAllRelatedInventoryRequestsItemTable.value.refresh()
  }

  watch([searchQuery, fileNoQuery, itemFileNoQuery, singleItemFileNo, statusFilter, userFilter, categoryFilter], () => {
    if (searchQuery.value === '') {
      singleItemFileNo.value = ''
    }
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchRelatedInventoryRequestHistory = (ctx, callback) => {
    store
      .dispatch('app-all-inventory-requests/fetchRelatedInventoryRequestHistoryFileNo', {
        search: searchQuery.value,
        fileNo: fileNoQuery.value,
        itemFileNo: itemFileNoQuery.value,
        singleItemFileNo: singleItemFileNo.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        createdBy: userFilter.value,
        category: categoryFilter.value,
        status: statusFilter.value,
        currentRequest: currentRequest.value,
      },
      { root: true })
      .then(response => {
        const { relatedRequestItems, total } = response.data
        inventoryRequestItemData.value = relatedRequestItems
        callback(relatedRequestItems)
        totalInventoryRequests.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              fileNo: fileNoQuery.value,
              itemFileNo: itemFileNoQuery.value,
              singleItemFileNo: singleItemFileNo.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              createdBy: userFilter.value,
              category: categoryFilter.value,
              status: statusFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveInventoryRequestStatusVariant = status => {
    if (status === 'completed') return 'success'
    if (status === 'cancelled') return 'danger'
    if (status === 'returned') return 'warning'
    if (status === 'collected') return 'secondary'
    if (status === 'open') return 'info'
    return 'primary'
  }

  return {
    fetchRelatedInventoryRequestHistory,
    tableColumns,
    perPage,
    currentPage,
    totalInventoryRequests,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    fileNoQuery,
    itemFileNoQuery,
    singleItemFileNo,
    refAllRelatedInventoryRequestsItemTable,

    resolveInventoryRequestStatusVariant,
    refetchData,

    inventoryRequestItemData,

    // Extra Filters
    categoryFilter,
    userFilter,
    statusFilter,
    currentRequest,
  }
}
